import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "react-bootstrap"
import { FiChevronRight } from "react-icons/fi"
import { FieldGroup1, FieldGroup2, StarRating, TagChangerTypeAhead, TextInput } from "src/common/components/InputFields";
import { BsChevronRight, BsStarFill, BsTrashFill } from "react-icons/bs";
import { actionClearProgressMessage, actionSetErrorMessage, actionSetInfoMessage, actionSetProgressMessage, AppMessagesContext, Localization, PersistenceObjectType, PersistenceScope, SessionContext, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { MitLatLng } from "src/managers/MapFacade";
import { LayerFunc } from "src/managers/LayerFunc";
import { actionAddDataLayer, MapitStateContext } from "src/states/MapitState";
import { ProjectContext, TEjendomsMetaData, removePersistentTag, addPersistentTag, addTag, addValue, handleRemoveEjendom, removeTag, removeValue, setState, useSetupProjectState, setInfo, ProjectState, handleToBeAdded, handleAddEjendom } from "src/states/ProjectState";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { IoClose } from "react-icons/io5";
import { RightPaneContext, RightPaneModus, actionGoToModus, actionToggleModus } from "src/states/RightPaneState";
import { SheetFunc } from "src/managers/SheetFunc";
import { ExploreSearchContext } from "src/states/ExploreSearchState";
import { AddressInterface } from "src/managers/AddressInterface";
import { ExportData } from "src/managers/ExportData";
import { MitStorage } from "src/managers/MitStorage";
import ReactDOM, { createPortal } from "react-dom";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { PropertyInfoView } from "src/propertyInfoTemplates/PropertyInfoView";
import { GlassButton } from "./GlassButtons";
import { ADModalBody, ADModalBodyNoScroll, ADModalFooter, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassCard } from "./MitGlassComponents";
import { closeWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";

async function ProjStateMigration(state: ProjectState) {
  // Migration from 18/12-2023
  let newData = (state.data?.filter((a) => a.latLng).map(async (a) => {
    if ("id" in a) {
      if (typeof a.id == "string" && a.id.length === 7) {
        a.bfeNr = parseInt(a.id);
      }
    }
    if (!a.bfeNr) {
      let resp = await AddressInterface.reverseGeocodeFull(a.latLng[0], a.latLng[1]);
      let addrData = await resp.json();
      let jordstykke = await (await fetch(addrData.jordstykke.href)).json();
      a.bfeNr = jordstykke.bfenummer;
    }
    return { ...a };
  }))
  return { ...state, data: await Promise.all(newData) }
}

export function ProjectManager({ children }) {
  const { state: SessionState } = useContext(SessionContext);
  const ProjectState = useSetupProjectState(SessionState);
  const { dispatch: rightPaneDispatch } = useContext(RightPaneContext);
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const [lastUser, setLastUser] = useState<string>("");

  useEffect(() => {
    if (SessionState.userRef + SessionState.customerRef !== lastUser) {
      setLastUser(SessionState.userRef + SessionState.customerRef)
      ProjectState.dispatch(setState({ isLoading: true }))

      let persistLayer = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket", ""))

      persistLayer.getObject(PersistenceScope.User, PersistenceObjectType.Settings, SessionState.customerRef, SessionState.userRef, "Projects.json").then((resutlJson) => {
        return JSON.parse(resutlJson.Body.toString())
      }).then((state) => {
        ProjStateMigration(state).then((migratedState) => {
          ProjectState.dispatch(setState(
            { ...migratedState, user: { userRef: SessionState.userRef, customerRef: SessionState.customerRef }, isLoading: false, isEmpty: false, isLoaded: true }
          ))
        })
      }).catch((a) => {
        if (typeof a === "string" && a.includes("NoSuchKey")) {
          ProjectState.dispatch(setState({ isEmpty: true, isLoaded: true, isLoading: false, user: { userRef: SessionState.userRef, customerRef: SessionState.customerRef } }))
          return
        } else if ("code" in a && a["code"] == "NoSuchKey") {
          ProjectState.dispatch(setState({ isEmpty: true, isLoaded: true, isLoading: false, user: { userRef: SessionState.userRef, customerRef: SessionState.customerRef } }))
          return
        }
        ProjectState.dispatch(setState({ isEmpty: true, isLoaded: true, isLoading: false, user: { userRef: SessionState.userRef, customerRef: SessionState.customerRef } }))
        let msg = a?.msg ?? a
        appMessageDispatch(actionSetErrorMessage(
          "Loading Project" + msg
        ))
      })

    }

  }, [SessionState])


  return (
    <>
      {ProjectState.state.info ?
        <AdvancedDragModal
          PosDefault={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)" }}
          onClose={() => { ProjectState.dispatch(setInfo()) }}
          title={""}
        >
          <ADModalInfo >{ProjectState.state.info}</ADModalInfo>

          <ADModalFooter>
            <GlassButton style={{ marginInline: "auto" }} onClick={() => {
              windowDispatch(closeWindow(WindowId.PropertyInformationDialog))
              ProjectState.dispatch(setInfo())
              rightPaneDispatch(actionGoToModus(RightPaneModus.Projects))
            }}>{Localization.getText("Go to Projects")}</GlassButton>
          </ADModalFooter>
        </AdvancedDragModal>
        : null}
      <ProjectContext.Provider
        value={ProjectState}
      >

        {ProjectState.state.toBeAdded?.length ?
          ReactDOM.createPortal(<AddWindow data={ProjectState.state.data} toAdd={ProjectState.state.toBeAdded} callBackOnSubmit={
            (a) => {
              a.forEach((ej) => {
                ProjectState.dispatch(handleAddEjendom(ej.bfeNr, ej.name, ej.latLng, ej.tags))

              });
              if (a.length > 1) {
                ProjectState.dispatch(
                  setInfo(Localization.getText("Property has been added"))
                );
              } else {
                ProjectState.dispatch(
                  setInfo(Localization.getFormattedText("{nr} properties have been added", { nr: a.length }))
                );
              }
              ProjectState.dispatch(handleToBeAdded([]))
            }
          } callBackOnCancel={() => ProjectState.dispatch(handleToBeAdded([]))} />, document.body)
          : <></>}
        {children}
      </ ProjectContext.Provider>
    </>
  )
}


export function Projects(props: { show: boolean }) {
  const { state: projState, dispatch: projDispatch } = useContext(ProjectContext);
  const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const { dispatch: rightPaneDispatch } = useContext(RightPaneContext)
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { state: explorerSearchState } = useContext(ExploreSearchContext);

  let [viewTag, setViewTag] = useState<string[]>([]);
  let [viewEjn, setViewEjn] = useState<number[]>([]);

  let uploadRef = useRef<HTMLInputElement>(null);
  let linkRef = useRef<HTMLAnchorElement>(null);

  let [dataEjn, setDataEjn] = useState<{ bfeNr?: number, dot?: { latlng: MitLatLng } } | null>(null);

  let [showProps, setShowProps] = useState<boolean>(false);
  let [showEjn, setShowEjn] = useState<boolean>(true);



  function handleSelTag(e: MouseEvent | undefined, key: string | null) {
    if (key == null) {
      setViewTag([])
      return
    }

    if (viewEjn && key) {
      let x: number[] = []
      viewEjn.forEach((view) => {
        let safeCurrent = projState.data.find((a) => a.bfeNr === view)
        if (safeCurrent && safeCurrent.tags.includes(key)) {
          x = [...x, view]
        }
      })
      setViewEjn(x)
    }
    if (e?.ctrlKey || e?.shiftKey) {
      if (viewTag.includes(key)) {
        setViewTag((a) => a.filter((a) => a !== key));
      } else {
        setViewTag((a) => [...a, key])
      }
    } else {
      setViewTag([key]);
    }
  }

  function saveToXLSX() {

  }

  function saveToFile() {
    let { data, persistentTags, version } = projState

    var file = new Blob([JSON.stringify({ version, persistentTags, data })], { type: "json" });
    if (linkRef.current) {
      linkRef.current.href = URL.createObjectURL(file);
      linkRef.current.setAttribute('target', '_blank');
      linkRef.current.download = "Project.json";
      linkRef.current.click()
    }
  }


  function V0ToV1Migration(a: any, state: ProjectState): ProjectState {
    if (Array.isArray(a)) {
      return {
        version: 1,
        isLoaded: true,
        isLoading: false,
        persistentTags: [],
        toBeAdded: [],
        isEmpty: false,
        ViamapPersistenceLayer: state.ViamapPersistenceLayer,
        user: state.user,
        info: "Old version migrated",
        data: a.map((a) => {
          if (a.coords) {
            let coords = a.coords
            delete a.coords
            return { ...a, latLng: coords?.reverse?.() }
          }
          return a
        }).filter(a => a.latLng)
      }
    } else {
      return a
    }
  }

  function importFile(e: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) {
    let x = (e as React.ChangeEvent<HTMLInputElement>).target?.files?.[0] || (e as React.DragEvent<HTMLElement>).dataTransfer.files?.[0]
    if (x) {
      x.text().then((a) => {
        projDispatch(setState(V0ToV1Migration(JSON.parse(a), projState)))
      })
    }
    if (uploadRef.current) {
      uploadRef.current.value = ""
      uploadRef.current.type = ""
      uploadRef.current.type = "file"
    }
    e.preventDefault();
    e.stopPropagation();
  }

  function handleSelEjendom(e: MouseEvent, bfeNr: number) {
    if (e?.ctrlKey || e?.shiftKey) {
      if (viewEjn.includes(bfeNr)) {
        setViewEjn((a) => a.filter((a) => a !== bfeNr));
      } else {
        setViewEjn((a) => [...a, bfeNr])
      }
    } else {
      setViewEjn([bfeNr]);
    }
  }

  function handleCreateLayerForTag(viewTag: string[], ejnInTag: TEjendomsMetaData[]) {
    if (!viewTag) {
      return
    }
    let points = ejnInTag.map((val) => {
      return {
        name: val.name,
        latlng: new MitLatLng(val.latLng[0], val.latLng[1]),
        properties: {
          // "tags": val.tags.join(', '),
          "description": val.cf_siteDescA,
          "zoning": val.cf_siteDescB,
          "starsOverall": val.cf_starAcc,
          "starsVisibility": val.cf_starVis
        }
      }
    })
    let layerInfo = LayerFunc.createLayerInfoForManyPoints(viewTag.toString(), points);
    mapitStateDispatch(actionAddDataLayer(layerInfo, false, true));
    appMessageDispatch(actionSetInfoMessage(Localization.getFormattedText("New map layer created from project {project}", { project: viewTag })));
  }

  async function handleExportExcelForTag(viewTag: string[], ejnInTag: TEjendomsMetaData[]) {
    if (!viewTag) {
      return
    }

    try {

      let createdDate = new Date();
      let count = ejnInTag.length;
      let wb = await (new ExportData()).createExcelExportForProject(viewTag.toString(), ejnInTag, createdDate, explorerSearchState.selectableFilterList,
        (progress: number) => {
          appMessageDispatch(actionSetProgressMessage("Export", Math.round(100 * progress / count)));
        });
      appMessageDispatch(actionClearProgressMessage());
      let blob = SheetFunc.createBlob(wb);
      let fileName = "ProjectExport_" + viewTag + "_" + createdDate.toLocaleDateString() + "_" + createdDate.toLocaleTimeString();
      fileName += ".xlsx";
      ExportData.downloadBlob(fileName, blob);
      appMessageDispatch(actionSetInfoMessage(Localization.getFormattedText("Project {project} has been exported to excel", { project: viewTag + "" })));
    }
    catch (error: any) {
      appMessageDispatch(actionSetErrorMessage(error));
    }
  }

  useEffect(() => {
    let x = MitStorage.getValue<{ showProps: boolean, showEjn: boolean }>("ProjPersist")
    if (x) {
      const { showProps, showEjn } = x
      setShowProps(showProps);
      setShowEjn(showEjn);
    }
  }, [])

  useEffect(() => {
    MitStorage.setValue("ProjPersist", { showProps: showProps, showEjn: showEjn })
  }, [showProps, showEjn])

  useEffect(() => {
    if (viewEjn.length == 1) {
      const selected = projState.data.filter((a) => a.bfeNr === viewEjn[0])[0] || projState.data[0]
      const mitLatLng = new MitLatLng(selected?.latLng?.[0] || 0, selected?.latLng?.[1] || 0)
      const data = {
        bfeNr: selected.bfeNr,
        dot: { latlng: mitLatLng }
      }
      setDataEjn(data)
    } else {
      const data = {
        id: undefined,
        dot: undefined
      }
      setDataEjn(data)
    }
  }, [viewEjn])

  function removeTag(e) {
    const tag = e.dataTransfer.getData("text/plain");
    projDispatch(removePersistentTag(tag))
    e.preventDefault();
  }

  function deleteEjendom(DeleteID: number) {
    let safeCurrent = projState.data.filter((a) => a.bfeNr === DeleteID)[0];
    if (safeCurrent) {
      projDispatch(handleRemoveEjendom(DeleteID));
      if (!safeCurrent.tags.length) {
        setViewEjn([])
      }
    }
  }

  const EjnInTag = projState.data.filter((a) => (viewTag.length === 0 || viewTag.every((Tag) => a.tags.includes(Tag)) || viewTag.includes("untagged") && a.tags.length === 0))
  // const SellEjn = projState.data.filter((a) => a.bfeNr === viewEjn)[0] || projState.data[0]

  if (!props.show) {
    return <></>
  }

  return (
    <>
      {projState.isEmpty && projState.isLoaded ? (
        <ConfirmationDialog showWindow={1000} header={""} onCancel={() => projDispatch(setState({ isLoaded: false }))} onSubmit={() => projDispatch(setState({ isEmpty: false }))} >
          {Localization.getText("An Existing Project cound't be found, create new?")}
        </ConfirmationDialog>
      ) : null}
      <input type="file" ref={uploadRef} onChange={(e) => importFile(e)} style={{ display: "none" }} />
      <a ref={linkRef} style={{ opacity: 0, position: "absolute" }}></a>
      {createPortal(
        <AdvancedDragModal

          PosDefault={{ top: "10%", left: "50%", width: "1600px", height: "100%", transform: "translateX(-50%)" }}
          title={Localization.getText("Projects")}
          onClose={() => rightPaneDispatch(actionToggleModus(RightPaneModus.Projects))}
          subHeaderContent={
            <>
              {viewTag && EjnInTag.length ?
                <ViewButtonIcon title={Localization.getText("Export selected tag to Excel")} onClick={() => handleExportExcelForTag(viewTag, EjnInTag)} ><img src="./images/NewSecSVG/excel.svg"></img></ViewButtonIcon>
                :
                <ViewButtonIcon disabled onClick={() => saveToXLSX()} ><img src="./images/NewSecSVG/excel.svg"></img></ViewButtonIcon>
              }
              <ViewButtonIcon title={Localization.getText("Export all projects to Json")} onClick={() => saveToFile()} ><img src="./images/NewSecSVG/json.svg"></img></ViewButtonIcon>
              <ViewButtonIcon title={Localization.getText("Import and overwrite all projects from Json")} onClick={() => uploadRef.current?.click()} onDrop={(e) => importFile(e)} ><img src="./images/NewSecSVG/importer.svg"></img></ViewButtonIcon>
            </>
          }
        >
          <ADModalBodyNoScroll
            className={"Projects"}
            templateRows={"15% 35% 50%"}
          >
            <div className="glassColumn">
              <GlassCard
                variant={"Flex1"}
                title={<>
                  <span style={{ color: "#454659" }} onClick={() => handleSelTag(undefined, null)}>{Localization.getText("Tag")}</span>
                  <Button
                    onClick={(e) => { viewTag && viewTag.forEach((tag) => projDispatch(removePersistentTag(tag))); e.preventDefault() }}
                    onDrop={removeTag} onDragOver={(e) => e.preventDefault()} size="sm" variant="alert" title={Localization.getText("Drop empty tag here to remove")} style={{ fontSize: "18px", marginLeft: "auto" }} ><BsTrashFill /></Button></>
                }
              >


                <TagList viewtag={viewTag} persistentTags={projState.persistentTags} tags={[...projState.data.reduce((a: string[], b) => [...b.tags.filter((c) => !a.includes(c)), ...a], projState.persistentTags || []).sort().filter((a) => a !== "untagged"), "untagged"]} callBackClick={handleSelTag} />
                <input style={{ background: "#e0e4e8", textAlign: "center", color: "#96a9bc", marginTop: "4px", boxShadow: "none", width: "100%" }} type="text" placeholder={Localization.getText("+ add new tag")} defaultValue={""} onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (e.currentTarget.value.trim()) {
                      projDispatch(addPersistentTag(e.currentTarget.value.trim()))
                      e.currentTarget.value = ""
                    }
                  }
                }} />
              </GlassCard>
            </div>
            <div className="glassColumn">
              <GlassCard
                variant={"Flex1"}
                title={<>
                  <div style={{ color: "#454659", marginRight: "auto" }}>{Localization.getText("Items")}</div>
                  {/* <Button style={{opacity: 1}} onClick={() => handleExportExcelForTag(viewTag, EjnInTag)} disabled={Boolean(!viewTag || viewTag === "untagged" || !EjnInTag.length)} size="sm" variant="alert" >{Localization.getText("Export Excel")}</Button> */}
                  <Button style={{ opacity: Number(!(viewTag.length == 0)) }} onClick={() => handleCreateLayerForTag(viewTag, EjnInTag)} disabled={Boolean(!(viewTag.length || viewTag.includes("untagged")))} size="sm" variant="alert" >{Localization.getText("Add as layer")}</Button>
                  <div style={{ color: "#454659", marginLeft: "auto" }}>({EjnInTag.length})</div>
                </>}
              >
                <EjendomList active={viewEjn} EjnList={EjnInTag} callBackClick={(e, bfe) => handleSelEjendom(e, bfe)} />
              </GlassCard>
            </div>
            <div className="glassColumn">
              <GlassCard
                variant={"Flex1"}
                title={
                  <>
                    <div>
                      <span style={{ position: "relative", textWrap: "nowrap", marginRight: "30px", color: "#454659" }} onClick={() => setShowEjn(!showEjn)}>{Localization.getText("Item")} <span className='mit-transformer' style={{ position: "absolute", marginLeft: "6px", transform: "Rotate(" + (showEjn ? "90deg" : "0deg") + ")" }}><BsChevronRight /></span></span>
                      <span style={{ position: "relative", textWrap: "nowrap", marginRight: "30px" }} onClick={() => setShowProps(!showProps)}>{Localization.getText("Property")}<span className='mit-transformer' style={{ position: "absolute", marginLeft: "6px", transform: "Rotate(" + (showProps ? "90deg" : "0deg") + ")" }}><BsChevronRight /></span></span>
                    </div><Button onClick={() => viewEjn && viewEjn.forEach((a) => deleteEjendom(a))} disabled={viewEjn.length === 0} size="sm" variant="alert" style={{ fontSize: "18px", marginLeft: "auto", opacity: Number(viewEjn.length !== 0) }} ><BsTrashFill /></Button>
                  </>
                }
              >


                {showEjn ? <EjendomView EjnList={projState.data.filter((a) => viewEjn.includes(a.bfeNr))} tagList={projState.data.reduce((a: string[], b) => [...b.tags.filter((c) => !a.includes(c)), ...a], []).sort().filter((a) => a !== "untagged")} /> : null}
                {/* {(dataEjn?.dot && showProps) ? TODO:
                  <PropertyInfoView
                    key={dataEjn.dot.latlng.lat.toString()}
                    foldetUd={true}
                    cols={1}
                    labelLatLng={dataEjn.dot}
                    callBackOnLoaded={() => { }}
                    allowExtendedInformation={true}
                  /> : null} */}
              </GlassCard>
            </div>
          </ADModalBodyNoScroll>
        </AdvancedDragModal>
        , document.getElementById("Mit-NonBlockingOverlay") || document.body)}
    </>
  )
}

function TagSelector({ name, onClick, ...rest }) {
  return (<div {...rest} tabIndex={0}><button onClick={onClick} style={{ textAlign: "left" }}  >{name}</button>
    {/* <button title={
  !persist ? Localization.getText("Unlock - Remove tag when empty") : Localization.getText("Locked - Keep tag even when empty")
} onClick={onPersist}>{!persist ? <HiLockOpen /> : <HiLockClosed />}</button> */}
  </div>)
}
function TagList(props: { persistentTags: string[], tags: string[], viewtag: string[], callBackClick: (e: MouseEvent, a: string) => void }) {
  const { dispatch: projDispatch } = useContext(ProjectContext);

  function onDragHandler(e, tag) {
    e.dataTransfer.setData("text/plain", tag)
  }

  return (
    <div className="TagList" style={{ display: "flex", flexDirection: "column", justifyContent: "stretch", flexWrap: "wrap" }} >
      {props.tags.map((a, index) => <TagSelector key={a} onPersist={() => { props.persistentTags.includes(a) ? projDispatch(removePersistentTag(a)) : projDispatch(addPersistentTag(a)) }} persist={props.persistentTags.includes(a)} name={index === props.tags.length - 1 ? Localization.getText("untagged") : a} className={props.viewtag && props.viewtag.includes(a) ? "active" : ""} onDragStart={(e) => onDragHandler(e, a)} draggable="true" onClick={(e) => props.callBackClick(e, a)} />)}
    </div>
  )
}

function StarViewer(stars: number | null) {
  let fixedStar = stars || 0
  return (
    <div className="SmallStar">
      {[1, 2, 3, 4, 5].map((a) => <BsStarFill key={a} style={{ color: (fixedStar && fixedStar >= a ? "#bd9060" : "#816c5d") }} />)}
    </div>
  )

}


function ListItem({ value, ...rest }) {
  const { dispatch: projDispatch } = useContext(ProjectContext)

  return (
    <div {...rest} style={{ cursor: "pointer", flex: "95%", display: "flex", justifyContent: "space-between" }}>
      <div>{value.name.split?.(",")[0]},</div>
      <div>{value.name.split?.(",").at(-1)}</div>
      {value.cf_starAcc ? <div>{StarViewer(value.cf_starAcc)} </div> : <div><FiChevronRight /></div>}
      <div className="TagsInList">{value.tags.map((b) => <span key={b}><button className="rmTag" onClick={(e) => { projDispatch(removeTag(value.bfeNr, b)); e.preventDefault(); e.stopPropagation() }}><IoClose /></button>{b}</span>)}</div>
    </div>
  )
}

function MultiItemView({ Ejendomme, tagList, ...rest }) {
  const { dispatch } = useContext(ProjectContext)

  const callbacks = {
    addV: (id, cfKey, b) => { Ejendomme.forEach(element => { dispatch(addValue(element.bfeNr, cfKey, b)) }) },
    remV: (id, cfKey) => { Ejendomme.forEach(element => { dispatch(removeValue(element.bfeNr, cfKey)) }) },
    remT: (id, a) => {
      Ejendomme.forEach(element => {
        dispatch(removeTag(element.bfeNr, a))
      });
    },
    addT: (id, a) => {
      Ejendomme.forEach(element => {
        dispatch(addTag(element.bfeNr, a))
      });
    },
  }

  return (
    <>
      <FieldGroup1 title={Localization.getText("Items")} >
        <h6>{Ejendomme.length} Selected</h6>
      </FieldGroup1>
      <FieldGroup1 title={Localization.getText("Tag")} >
        <TagChangerTypeAhead id="UnknonwnID" value={{ tags: [...new Set(Ejendomme.flatMap((a) => a.tags))], bfeNr: 1001010 }} options={tagList} callbacks={callbacks} {...rest} />
      </FieldGroup1>

    </>
  )
}

function ItemView({ id, value, tagList, ...rest }) {
  const { dispatch } = useContext(ProjectContext)

  const callbacks = {
    addV: (id, cfKey, b) => { dispatch(addValue(id, cfKey, b)) },
    remV: (id, cfKey) => { dispatch(removeValue(id, cfKey)) },
    remT: (id, a) => { dispatch(removeTag(id, a)) },
    addT: (id, a) => { dispatch(addTag(id, a)) },
  }

  return (
    <>
      <FieldGroup1 title={Localization.getText("Address")} >
        <h6>{value.name}</h6>
        {/* <div><h6>Created</h6><div>{new Date(value.created).toLocaleDateString()}</div></div> */}
        {/* <div><h6>Changed</h6><div>{new Date(value.changed).toLocaleDateString()}</div></div> */}
      </FieldGroup1>
      {/* <FieldGroup1 title="Position" > */}
      {/* <MapView  name={"TEST"} {...{id, value, callbacks, ...rest}} /> */}
      {/* </FieldGroup1> */}
      <FieldGroup1 title={Localization.getText("Tag")} >
        <TagChangerTypeAhead {...{ id, value, options: tagList, callbacks, ...rest }} />
      </FieldGroup1>
      <FieldGroup1 title={Localization.getText("Site description")}>
        <TextInput value={value} cfKey={"cf_siteDescA"} callbacks={callbacks} />
      </FieldGroup1>
      <FieldGroup1 title={Localization.getText("Zoning")}>
        <TextInput value={value} cfKey={"cf_siteDescB"} callbacks={callbacks} />
      </FieldGroup1>
      <FieldGroup2 title={Localization.getText("Rating")} >
        <StarRating name={Localization.getText("Overall evaluation")} value={value} cfKey={"cf_starAcc"} callbacks={callbacks} />
        <StarRating name={Localization.getText("Visibility")} value={value} cfKey={"cf_starVis"} callbacks={callbacks} />
      </FieldGroup2>
    </>
  )
}
function EjendomView(props: { EjnList, tagList }) {

  if (props.EjnList.length > 1) {

    return (
      <div className="EjendomView" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }} >
        <MultiItemView Ejendomme={props.EjnList} tagList={props.tagList} />
      </div>
    )
  }

  return (
    <div className="EjendomView" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }} >
      {props.EjnList.map((a, b) => <ItemView key={a.bfeNr + b} id={a.bfeNr} value={a} tagList={props.tagList} />)}
    </div>
  )
}

function EjendomList(props: { EjnList, active, callBackClick: (e: MouseEvent, a: number) => void }) {
  const { dispatch } = useContext(ProjectContext)

  function DropHandler(bfeNr, e) {
    const tag = e.dataTransfer.getData("text/plain");
    const listTag = tag.split("	")
    listTag.forEach(element => {
      if (element.trim()) {
        dispatch(addTag(bfeNr, tag))
      }
    }); (() => { })

    e.preventDefault();
  }

  return (
    <div className="Project list" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }} >
      {props.EjnList.map((a, b) => <ListItem key={a.bfeNr} id={b} onDragOver={(e) => e.preventDefault()} onDrop={(e) => DropHandler(a.bfeNr, e)} value={a} className={(props.active.includes(a.bfeNr) ? "active" : "") + " ResItem Item"} onClick={(e) => props.callBackClick(e, a.bfeNr)} />)}
    </div>
  )
}

// function MapView({name, value ,callbacks, ...rest}) {
//   let mD:DemographyReportMetaData = {
//     center: new MitLatLng(value.coords[1], value.coords[0]),
//     radiusMeters:50,
//     logo:"",
//     reportDate: new Date(),
//     languageCode: "da",
//     reportName: "Non"
//   }
//   return <EmbeddedSimpleMap metaData={mD} showWindow={true} />
// }


function AddWindow(props: { data: any[], toAdd: any[], callBackOnSubmit?: (a: any) => void, callBackOnCancel?: () => void }) {
  let [loading, setLoading] = useState(true);
  let { state: projState } = useContext(ProjectContext)
  let { dispatch: modusDispatch } = useContext(RightPaneContext)
  let [selectedTags, setSelectedTags] = useState<string[]>([])

  useEffect(() => {
    if (props.toAdd.length > 0) {
      setLoading(false)
    } else {
      setLoading(true)
    }
  }, [props.toAdd])

  function handleSubmit() {
    try {


      const result = props.toAdd.map((a) => {
        if (!("bfeNr" in a && a.bfeNr)) {
          throw new Error("Missing bfeNr")
        }
        if (!("latLng" in a && a.latLng)) {
          throw new Error("Missing latLng")
        }
        if (!("name" in a && a.name)) {
          throw new Error("Missing name")
        }
        return { ...a, tags: selectedTags }
      })

      props.callBackOnSubmit?.(result)
    }
    catch (e: any) {
      console.error(e)
      console.error("Error")
    }
  }

  function addTag(bfe, tag) {
    setSelectedTags((a) => [...a, tag])
  }

  function remTag(bfe, tag: string) {
    setSelectedTags((a) => (a.filter((rTag) => rTag !== tag)))
  }

  const tagList = useMemo(() => {
    return [...props.data.reduce((a: string[], b) => [...b.tags.filter((c) => !a.includes(c)), ...a], []).sort().filter((a) => a !== "untagged"), "untagged"]
  }, [props.data])

  if (projState.isEmpty) {
    return (<AdvancedDragModal
      title={""}
      variant={"NSDark"}
      PosDefault={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)" }}
      onClose={props.callBackOnCancel}
    >
      <ADModalInfo >{Localization.getText("Please visit the Project tabs, an action is needed!")}</ADModalInfo>
      {/* <RRAINPUT freeForm={true} suggestions={tags} onBlur={setSelectedTags} /> */}
      <ADModalFooter>

        <GlassButton onClick={() =>
          modusDispatch(actionGoToModus(RightPaneModus.Projects))} >{Localization.getText("Go to Projects")}</GlassButton>
      </ADModalFooter>
      {/* <Button onClick={handleSubmit} >{Localization.getText("Add and go to Project")}</Button> */}
    </AdvancedDragModal>
    )
  }

  return (<AdvancedDragModal
    title={""}
    variant={"NSDark"}
    PosDefault={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)" }}
    onClose={props.callBackOnCancel}
  >
    {props.toAdd?.length !== 1 ?
      <ADModalInfo >{loading ? "Loading..." : Localization.getFormattedText(`Add {number} items to Project`, { number: props.toAdd.length })}</ADModalInfo>
      :
      <ADModalInfo >{loading ? "Loading..." : Localization.getText(`Add Property to Project`)}</ADModalInfo>
    }
    <ADModalBody>
      {/* <RRAINPUT freeForm={true} suggestions={tags} onBlur={setSelectedTags} /> */}
      {/* <div className="EjendomView" > */}
      <div style={{ marginTop: "10px" }}>
        <TagChangerTypeAhead id="ss" value={{ bfeNr: 0, tags: selectedTags }} options={tagList.filter((a) => a !== "untagged")} callbacks={{ remT: remTag, addT: addTag }} />
      </div>
    </ADModalBody>

    <ADModalFooter>
      <GlassButton onClick={handleSubmit} >{Localization.getText("Add")}</GlassButton>
    </ADModalFooter>
  </AdvancedDragModal>
  )

}
